// pages/Inventario/Inventario.js - Menu Inventário (COM DATA, ORDENAÇÃO E FILTRO POR ARMAZÉM)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import './Inventario.css';
import { Icon } from './SharedComponents';

const API_BASE_URL = '/api';

// ==============================================
// CONFIGURAÇÕES PADRÃO (fallback)
// ==============================================
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.'
    },
    formatoData: 'DD/MM/YYYY',
    timezone: 'Africa/Maputo',
    idioma: 'pt-PT',
    iva: 16
};

// ==============================================
// CONSTANTES
// ==============================================
const UNIDADES_MEDIDA = [
    { value: 'UN', label: 'Unidade' },
    { value: 'KG', label: 'Quilograma' },
    { value: 'L', label: 'Litro' },
    { value: 'M', label: 'Metro' },
    { value: 'CX', label: 'Caixa' },
    { value: 'PCT', label: 'Pacote' }
];

// ==============================================
// 🔥 FUNÇÃO PARA FORMATAR MOEDA COM CONFIGURAÇÃO
// ==============================================
const formatarMoedaPDF = (valor, config) => {
    const moeda = config?.moeda || CONFIG_PADRAO.moeda;
    const { simbolo = 'MT', casasDecimais = 2, separadorDecimal = ',', separadorMilhar = '.' } = moeda;

    if (valor === undefined || valor === null || isNaN(valor)) {
        return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
    }

    let numero = Number(valor);
    if (isNaN(numero)) {
        return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
    }

    numero = numero.toFixed(casasDecimais);
    const partes = numero.split('.');
    let inteiros = partes[0];
    const decimais = partes[1] || '0'.repeat(casasDecimais);

    inteiros = inteiros.replace(/\B(?=(\d{3})+(?!\d))/g, separadorMilhar);

    return `${inteiros}${separadorDecimal}${decimais} ${simbolo}`;
};

// ==============================================
// 🔥 FUNÇÃO PARA FORMATAR DATA
// ==============================================
const formatarData = (data) => {
    if (!data) return '-';
    const d = new Date(data);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const ano = d.getFullYear();
    const horas = String(d.getHours()).padStart(2, '0');
    const minutos = String(d.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
};

// ==============================================
// 🔥 FUNÇÃO PARA ORDENAR PRODUTOS POR DATA (MAIS RECENTES PRIMEIRO)
// ==============================================
const ordenarPorData = (lista) => {
    return [...lista].sort((a, b) => {
        const dataA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dataB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dataB.getTime() - dataA.getTime();
    });
};

// ==============================================
// 🔥 COMPONENTE PDF - INVENTÁRIO COM ARMAZÉM
// ==============================================
const InventarioPDFDocument = ({ produtos, stats, configEmpresa, filtroDescricao, empresaNome = 'Minha Empresa' }) => {
    const { Document, Page, Text, View, StyleSheet, Font } = require('@react-pdf/renderer');

    Font.register({
        family: 'Helvetica',
        fonts: [
            { src: 'https://fonts.gstatic.com/s/helvetica/v1/helvetica.woff2', fontWeight: 'normal' },
            { src: 'https://fonts.gstatic.com/s/helvetica/v1/helvetica-bold.woff2', fontWeight: 'bold' }
        ]
    });

    const colors = {
        primary: '#4f46e5',
        primaryLight: '#818cf8',
        primaryDark: '#3730a3',
        secondary: '#f59e0b',
        success: '#10b981',
        danger: '#dc2626',
        info: '#3b82f6',
        white: '#ffffff',
        gray50: '#f9fafb',
        gray100: '#f3f4f6',
        gray200: '#e5e7eb',
        gray300: '#d1d5db',
        gray400: '#9ca3af',
        gray500: '#6b7280',
        gray600: '#4b5563',
        gray700: '#374151',
        gray800: '#1f2937',
        gray900: '#111827',
        border: '#e5e7eb'
    };

    const styles = StyleSheet.create({
        page: {
            padding: 35,
            fontSize: 8,
            fontFamily: 'Helvetica',
            backgroundColor: colors.white,
        },
        header: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 15,
            paddingBottom: 10,
            borderBottomWidth: 2,
            borderBottomColor: colors.primary,
        },
        headerLeft: {
            flexDirection: 'column',
        },
        headerTitle: {
            fontSize: 18,
            fontWeight: 'bold',
            color: colors.primary,
            letterSpacing: 1,
        },
        headerSubtitle: {
            fontSize: 9,
            color: colors.gray500,
            marginTop: 2,
        },
        headerRight: {
            flexDirection: 'column',
            alignItems: 'flex-end',
        },
        headerDate: {
            fontSize: 8,
            color: colors.gray500,
        },
        headerCompany: {
            fontSize: 10,
            fontWeight: 'bold',
            color: colors.gray700,
        },
        title: {
            fontSize: 16,
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: 4,
            color: colors.gray800,
        },
        subtitle: {
            fontSize: 9,
            textAlign: 'center',
            marginBottom: 4,
            color: colors.gray500,
        },
        subtitleBold: {
            fontSize: 9,
            textAlign: 'center',
            marginBottom: 12,
            color: colors.primary,
            fontWeight: 'bold',
        },
        statsContainer: {
            flexDirection: 'row',
            justifyContent: 'space-around',
            marginBottom: 12,
            padding: 10,
            backgroundColor: colors.gray50,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: colors.gray200,
        },
        statItem: {
            flexDirection: 'column',
            alignItems: 'center',
            paddingHorizontal: 4,
        },
        statValue: {
            fontSize: 11,
            fontWeight: 'bold',
            color: colors.gray800,
        },
        statLabel: {
            fontSize: 6,
            color: colors.gray500,
            marginTop: 2,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
        },
        table: {
            width: '100%',
            marginTop: 5,
        },
        tableHeader: {
            flexDirection: 'row',
            backgroundColor: colors.primary,
            minHeight: 24,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
        },
        tableRow: {
            flexDirection: 'row',
            borderBottomWidth: 0.5,
            borderBottomColor: colors.gray200,
            minHeight: 20,
        },
        tableRowAlternate: {
            backgroundColor: colors.gray50,
        },
        tableCell: {
            padding: 4,
            fontSize: 6.5,
            textAlign: 'center',
            color: colors.gray700,
        },
        tableCellHeader: {
            padding: 5,
            fontSize: 6.5,
            fontWeight: 'bold',
            color: colors.white,
            textAlign: 'center',
        },
        // LARGURAS DAS COLUNAS - COM ARMAZÉM
        colData: { width: '9%' },
        colCodigo: { width: '7%' },
        colNome: { width: '14%' },
        colCategoria: { width: '10%' },
        colFornecedor: { width: '10%' },
        colArmazem: { width: '9%' },
        colStock: { width: '7%' },
        colPreco: { width: '11%' },
        colValor: { width: '12%' },
        colStatus: { width: '11%' },
        statusBadge: {
            padding: 2,
            borderRadius: 3,
            fontSize: 5.5,
            fontWeight: 'bold',
            textAlign: 'center',
        },
        statusSemStock: { backgroundColor: '#fee2e2', color: '#dc2626' },
        statusBaixoStock: { backgroundColor: '#fef3c7', color: '#f59e0b' },
        statusMedioStock: { backgroundColor: '#dbeafe', color: '#3b82f6' },
        statusAltoStock: { backgroundColor: '#d1fae5', color: '#10b981' },
        footer: {
            position: 'absolute',
            bottom: 25,
            left: 35,
            right: 35,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTopWidth: 1,
            borderTopColor: colors.gray200,
            paddingTop: 8,
        },
        footerText: {
            fontSize: 6,
            color: colors.gray400,
        },
        footerPage: {
            fontSize: 6,
            color: colors.gray400,
        },
        resumeContainer: {
            flexDirection: 'row',
            justifyContent: 'space-around',
            marginTop: 10,
            padding: 6,
            backgroundColor: colors.gray50,
            borderRadius: 4,
            borderWidth: 1,
            borderColor: colors.gray200,
        },
        resumeItem: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
        },
        resumeDot: {
            width: 8,
            height: 8,
            borderRadius: 4,
        },
        resumeLabel: {
            fontSize: 6,
            color: colors.gray500,
        },
        resumeValue: {
            fontSize: 6,
            fontWeight: 'bold',
            color: colors.gray700,
        },
    });

    const getStatusStyle = (stock, stockMinimo) => {
        if (stock === 0) return styles.statusSemStock;
        if (stock <= stockMinimo) return styles.statusBaixoStock;
        if (stock < 10) return styles.statusMedioStock;
        return styles.statusAltoStock;
    };

    const getStatusLabel = (stock, stockMinimo) => {
        if (stock === 0) return 'Sem Stock';
        if (stock <= stockMinimo) return 'Stock Baixo';
        if (stock < 10) return 'Stock Médio';
        return 'Stock Alto';
    };

    const getStatusColor = (stock, stockMinimo) => {
        if (stock === 0) return '#dc2626';
        if (stock <= stockMinimo) return '#f59e0b';
        if (stock < 10) return '#3b82f6';
        return '#10b981';
    };

    const dataAtual = new Date().toLocaleString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });

    const totalProdutos = produtos.length;
    const totalStock = produtos.reduce((sum, p) => sum + (p.stockAtual || 0), 0);
    const totalValorStock = produtos.reduce((sum, p) => sum + ((p.stockAtual || 0) * (p.precoVenda || 0)), 0);

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        <Text style={styles.headerTitle}>RAF ERP</Text>
                        <Text style={styles.headerSubtitle}>Sistema de Gestão Empresarial</Text>
                    </View>
                    <View style={styles.headerRight}>
                        <Text style={styles.headerCompany}>{empresaNome}</Text>
                        <Text style={styles.headerDate}>Gerado em: {dataAtual}</Text>
                    </View>
                </View>

                <Text style={styles.title}>Inventário de Produtos</Text>
                {filtroDescricao && (
                    <Text style={styles.subtitleBold}>Filtro: {filtroDescricao}</Text>
                )}

                <View style={styles.statsContainer}>
                    <View style={styles.statItem}>
                        <Text style={styles.statValue}>{totalProdutos}</Text>
                        <Text style={styles.statLabel}>Total Produtos</Text>
                    </View>
                    <View style={styles.statItem}>
                        <Text style={styles.statValue}>{totalStock}</Text>
                        <Text style={styles.statLabel}>Stock Total</Text>
                    </View>
                    <View style={styles.statItem}>
                        <Text style={styles.statValue}>{formatarMoedaPDF(totalValorStock, configEmpresa)}</Text>
                        <Text style={styles.statLabel}>Valor em Stock</Text>
                    </View>
                    <View style={styles.statItem}>
                        <Text style={styles.statValue}>{stats.categoriasCount || 0}</Text>
                        <Text style={styles.statLabel}>Categorias</Text>
                    </View>
                    <View style={styles.statItem}>
                        <Text style={styles.statValue}>{stats.fornecedoresCount || 0}</Text>
                        <Text style={styles.statLabel}>Fornecedores</Text>
                    </View>
                    <View style={styles.statItem}>
                        <Text style={styles.statValue}>{(stats.produtosSemStock || 0) + (stats.produtosBaixoStock || 0)}</Text>
                        <Text style={styles.statLabel}>Stock Crítico</Text>
                    </View>
                </View>

                <View style={styles.table}>
                    {/* CABEÇALHO COM ARMAZÉM */}
                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableCellHeader, styles.colData]}>Data</Text>
                        <Text style={[styles.tableCellHeader, styles.colCodigo]}>Código</Text>
                        <Text style={[styles.tableCellHeader, styles.colNome]}>Produto</Text>
                        <Text style={[styles.tableCellHeader, styles.colCategoria]}>Categoria</Text>
                        <Text style={[styles.tableCellHeader, styles.colFornecedor]}>Fornecedor</Text>
                        <Text style={[styles.tableCellHeader, styles.colArmazem]}>Armazém</Text>
                        <Text style={[styles.tableCellHeader, styles.colStock]}>Stock</Text>
                        <Text style={[styles.tableCellHeader, styles.colPreco]}>Preço Venda</Text>
                        <Text style={[styles.tableCellHeader, styles.colValor]}>Valor Stock</Text>
                        <Text style={[styles.tableCellHeader, styles.colStatus]}>Status</Text>
                    </View>

                    {/* LINHAS COM ARMAZÉM */}
                    {produtos.map((p, index) => {
                        const categoriaNome = p.categoriaId?.nome || '-';
                        const fornecedorNome = p.fornecedorId?.nome || '-';
                        const armazemNome = p.armazemId?.nome || '-';
                        const statusLabel = getStatusLabel(p.stockAtual, p.stockMinimo);
                        const valorStock = (p.stockAtual || 0) * (p.precoVenda || 0);
                        const isAlternate = index % 2 === 0;
                        const dataCriacao = p.createdAt ? formatarDataPDF(p.createdAt) : '-';

                        return (
                            <View style={[styles.tableRow, isAlternate && styles.tableRowAlternate]} key={p._id || index}>
                                <Text style={[styles.tableCell, styles.colData, { fontSize: 5.5 }]}>{dataCriacao}</Text>
                                <Text style={[styles.tableCell, styles.colCodigo]}>{p.codigo || '-'}</Text>
                                <Text style={[styles.tableCell, styles.colNome]}>{p.nome}</Text>
                                <Text style={[styles.tableCell, styles.colCategoria]}>{categoriaNome}</Text>
                                <Text style={[styles.tableCell, styles.colFornecedor]}>{fornecedorNome}</Text>
                                <Text style={[styles.tableCell, styles.colArmazem]}>{armazemNome}</Text>
                                <Text style={[styles.tableCell, styles.colStock, { fontWeight: 'bold', color: getStatusColor(p.stockAtual, p.stockMinimo) }]}>
                                    {p.stockAtual || 0}
                                </Text>
                                <Text style={[styles.tableCell, styles.colPreco]}>
                                    {formatarMoedaPDF(p.precoVenda, configEmpresa)}
                                </Text>
                                <Text style={[styles.tableCell, styles.colValor, { fontWeight: 'bold' }]}>
                                    {formatarMoedaPDF(valorStock, configEmpresa)}
                                </Text>
                                <View style={[styles.tableCell, styles.colStatus]}>
                                    <Text style={[styles.statusBadge, getStatusStyle(p.stockAtual, p.stockMinimo)]}>
                                        {statusLabel}
                                    </Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                <View style={styles.resumeContainer}>
                    <View style={styles.resumeItem}>
                        <View style={[styles.resumeDot, { backgroundColor: '#10b981' }]} />
                        <Text style={styles.resumeLabel}>Stock Alto</Text>
                        <Text style={styles.resumeValue}>{stats.produtosAltoStock || 0}</Text>
                    </View>
                    <View style={styles.resumeItem}>
                        <View style={[styles.resumeDot, { backgroundColor: '#3b82f6' }]} />
                        <Text style={styles.resumeLabel}>Stock Médio</Text>
                        <Text style={styles.resumeValue}>{stats.produtosMedioStock || 0}</Text>
                    </View>
                    <View style={styles.resumeItem}>
                        <View style={[styles.resumeDot, { backgroundColor: '#f59e0b' }]} />
                        <Text style={styles.resumeLabel}>Stock Baixo</Text>
                        <Text style={styles.resumeValue}>{stats.produtosBaixoStock || 0}</Text>
                    </View>
                    <View style={styles.resumeItem}>
                        <View style={[styles.resumeDot, { backgroundColor: '#dc2626' }]} />
                        <Text style={styles.resumeLabel}>Sem Stock</Text>
                        <Text style={styles.resumeValue}>{stats.produtosSemStock || 0}</Text>
                    </View>
                </View>

                <View style={styles.footer}>
                    <Text style={styles.footerText}>
                        Total de produtos: {totalProdutos} | {formatarMoedaPDF(totalValorStock, configEmpresa)} em stock
                    </Text>
                    <Text style={styles.footerPage}>
                        Página <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

// Função para formatar data no PDF
const formatarDataPDF = (data) => {
    if (!data) return '-';
    const d = new Date(data);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const ano = d.getFullYear();
    const horas = String(d.getHours()).padStart(2, '0');
    const minutos = String(d.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
};

export default function Inventario() {
    // ==============================================
    // ESTADOS PRINCIPAIS
    // ==============================================
    const [produtos, setProdutos] = useState([]);
    const [filteredProdutos, setFilteredProdutos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [configEmpresa, setConfigEmpresa] = useState(CONFIG_PADRAO);

    // ==============================================
    // FILTROS E PESQUISA
    // ==============================================
    const [searchTerm, setSearchTerm] = useState('');
    const [filtros, setFiltros] = useState({
        categoria: 'todos',
        fornecedor: 'todos',
        armazem: 'todos', // 🔥 NOVO FILTRO POR ARMAZÉM
        statusStock: 'todos',
        unidadeMedida: 'todos',
        ativo: 'todos',
        ordenarPor: 'nome'
    });
    const [showFiltros, setShowFiltros] = useState(false);

    // ==============================================
    // PAGINAÇÃO
    // ==============================================
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 9,
        total: 0,
        pages: 0
    });

    // ==============================================
    // DADOS AUXILIARES
    // ==============================================
    const [categorias, setCategorias] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);
    const [armazens, setArmazens] = useState([]); // 🔥 NOVO ESTADO PARA ARMAZÉNS
    const [loggedUser, setLoggedUser] = useState(null);
    const [empresaId, setEmpresaId] = useState(null);
    const [empresaNome, setEmpresaNome] = useState('Minha Empresa');

    // ==============================================
    // ESTATÍSTICAS
    // ==============================================
    const [stats, setStats] = useState({
        totalProdutos: 0,
        totalStock: 0,
        valorTotalStock: 0,
        categoriasCount: 0,
        fornecedoresCount: 0,
        armazensCount: 0, // 🔥 NOVA ESTATÍSTICA
        produtosSemStock: 0,
        produtosBaixoStock: 0,
        produtosMedioStock: 0,
        produtosAltoStock: 0
    });

    // ==============================================
    // ESTADOS ADICIONAIS
    // ==============================================
    const [message, setMessage] = useState({ text: '', type: '' });
    const [exportLoading, setExportLoading] = useState(false);

    // ==============================================
    // 🔥 MODAL DE EXPORTAÇÃO
    // ==============================================
    const [showExportModal, setShowExportModal] = useState(false);
    const [exporting, setExporting] = useState(false);

    // ==============================================
    // API INSTANCE
    // ==============================================
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache'
        }
    });

    // ==============================================
    // FUNÇÕES AUXILIARES
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const obterEmpresaIdDoUtilizador = (user) => {
        if (!user) return null;
        let id = extrairEmpresaId(user.empresaId);
        if (id) return id;
        id = extrairEmpresaId(user.empresa);
        if (id) return id;
        id = extrairEmpresaId(user.empresaAtual);
        if (id) return id;
        if (user.empresas && Array.isArray(user.empresas) && user.empresas.length > 0) {
            id = extrairEmpresaId(user.empresas[0]);
            if (id) return id;
        }
        return null;
    };

    const getStatusStock = (stock, stockMinimo = 0) => {
        if (stock === 0) return { label: 'Sem Stock', color: '#dc2626', icon: 'block', class: 'sem-stock' };
        if (stock <= stockMinimo) return { label: 'Stock Baixo', color: '#f59e0b', icon: 'warning', class: 'baixo-stock' };
        if (stock < 10) return { label: 'Stock Médio', color: '#3b82f6', icon: 'info', class: 'stock-medio' };
        return { label: 'Stock Alto', color: '#10b981', icon: 'check_circle', class: 'stock-alto' };
    };

    const getUnidadeLabel = (value) => {
        const found = UNIDADES_MEDIDA.find(u => u.value === value);
        return found ? found.label : value || '-';
    };

    const formatarMoeda = (valor) => {
        const moeda = configEmpresa?.moeda || CONFIG_PADRAO.moeda;
        const { simbolo = 'MT', casasDecimais = 2, separadorDecimal = ',', separadorMilhar = '.' } = moeda;

        if (valor === undefined || valor === null || isNaN(valor)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        let numero = Number(valor);
        if (isNaN(numero)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        numero = numero.toFixed(casasDecimais);
        const partes = numero.split('.');
        let inteiros = partes[0];
        const decimais = partes[1] || '0'.repeat(casasDecimais);

        inteiros = inteiros.replace(/\B(?=(\d{3})+(?!\d))/g, separadorMilhar);

        return `${inteiros}${separadorDecimal}${decimais} ${simbolo}`;
    };

    const formatarNumero = (valor) => {
        if (valor === undefined || valor === null) return '0';
        const num = Number(valor);
        if (isNaN(num)) return '0';

        if (num >= 1e9) {
            return (num / 1e9).toFixed(1) + 'B';
        }
        if (num >= 1e6) {
            return (num / 1e6).toFixed(1) + 'M';
        }
        if (num >= 1e3) {
            return (num / 1e3).toFixed(1) + 'k';
        }

        return num.toLocaleString('pt-PT');
    };

    const showToast = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    // ==============================================
    // CALCULAR ESTATÍSTICAS
    // ==============================================
    const calcularStats = (produtosList) => {
        if (!produtosList || produtosList.length === 0) {
            setStats({
                totalProdutos: 0,
                totalStock: 0,
                valorTotalStock: 0,
                categoriasCount: 0,
                fornecedoresCount: 0,
                armazensCount: 0,
                produtosSemStock: 0,
                produtosBaixoStock: 0,
                produtosMedioStock: 0,
                produtosAltoStock: 0
            });
            return;
        }

        const totalProdutos = produtosList.length;
        const totalStock = produtosList.reduce((sum, p) => sum + (p.stockAtual || 0), 0);
        const valorTotalStock = produtosList.reduce((sum, p) => sum + ((p.stockAtual || 0) * (p.precoVenda || 0)), 0);

        const categoriasSet = new Set();
        const fornecedoresSet = new Set();
        const armazensSet = new Set(); // 🔥 NOVO
        let semStock = 0;
        let baixoStock = 0;
        let medioStock = 0;
        let altoStock = 0;

        produtosList.forEach(p => {
            let categoriaId = null;
            if (p.categoriaId) {
                if (typeof p.categoriaId === 'string') {
                    categoriaId = p.categoriaId;
                } else if (typeof p.categoriaId === 'object') {
                    categoriaId = p.categoriaId._id || p.categoriaId.id || null;
                }
            }
            if (categoriaId) {
                categoriasSet.add(categoriaId);
            }

            let fornecedorId = null;
            if (p.fornecedorId) {
                if (typeof p.fornecedorId === 'string') {
                    fornecedorId = p.fornecedorId;
                } else if (typeof p.fornecedorId === 'object') {
                    fornecedorId = p.fornecedorId._id || p.fornecedorId.id || null;
                }
            }
            if (fornecedorId) {
                fornecedoresSet.add(fornecedorId);
            }

            // 🔥 EXTRAIR ARMAZÉM
            let armazemId = null;
            if (p.armazemId) {
                if (typeof p.armazemId === 'string') {
                    armazemId = p.armazemId;
                } else if (typeof p.armazemId === 'object') {
                    armazemId = p.armazemId._id || p.armazemId.id || null;
                }
            }
            if (armazemId) {
                armazensSet.add(armazemId);
            }

            const status = getStatusStock(p.stockAtual, p.stockMinimo);
            if (status.class === 'sem-stock') semStock++;
            else if (status.class === 'baixo-stock') baixoStock++;
            else if (status.class === 'stock-medio') medioStock++;
            else if (status.class === 'stock-alto') altoStock++;
        });

        setStats({
            totalProdutos,
            totalStock,
            valorTotalStock,
            categoriasCount: categoriasSet.size,
            fornecedoresCount: fornecedoresSet.size,
            armazensCount: armazensSet.size,
            produtosSemStock: semStock,
            produtosBaixoStock: baixoStock,
            produtosMedioStock: medioStock,
            produtosAltoStock: altoStock
        });
    };

    // ==============================================
    // BUSCAR DADOS
    // ==============================================
    const fetchLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    setLoggedUser(storedUser);
                    const empresaIdExtraido = obterEmpresaIdDoUtilizador(storedUser);
                    setEmpresaId(empresaIdExtraido);

                    let nomeEmpresa = 'Minha Empresa';

                    if (storedUser.empresaNome) {
                        nomeEmpresa = storedUser.empresaNome;
                    } else if (storedUser.empresa && storedUser.empresa.nome) {
                        nomeEmpresa = storedUser.empresa.nome;
                    } else if (storedUser.empresaId && typeof storedUser.empresaId === 'object' && storedUser.empresaId.nome) {
                        nomeEmpresa = storedUser.empresaId.nome;
                    } else if (storedUser.empresaAtual && storedUser.empresaAtual.nome) {
                        nomeEmpresa = storedUser.empresaAtual.nome;
                    } else if (empresaIdExtraido) {
                        try {
                            const empresaResponse = await axios.get(`${API_BASE_URL}/empresas/${empresaIdExtraido}`, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                            if (empresaResponse.data && empresaResponse.data.success) {
                                const empresaData = empresaResponse.data.data || empresaResponse.data.empresa;
                                if (empresaData && empresaData.nome) {
                                    nomeEmpresa = empresaData.nome;
                                }
                            }
                        } catch (err) {
                            console.warn('⚠️ Não foi possível buscar o nome da empresa:', err.message);
                        }
                    }

                    setEmpresaNome(nomeEmpresa);
                    console.log('🏢 Nome da empresa (storedUser):', nomeEmpresa);
                    return storedUser;
                }
                return null;
            }

            const response = await axios.get(`${API_BASE_URL}/auth/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                const userData = response.data.data;
                setLoggedUser(userData);
                const empresaIdExtraido = obterEmpresaIdDoUtilizador(userData);
                setEmpresaId(empresaIdExtraido);

                let nomeEmpresa = 'Minha Empresa';

                if (userData.empresaNome) {
                    nomeEmpresa = userData.empresaNome;
                } else if (userData.empresa && userData.empresa.nome) {
                    nomeEmpresa = userData.empresa.nome;
                } else if (userData.empresaId && typeof userData.empresaId === 'object' && userData.empresaId.nome) {
                    nomeEmpresa = userData.empresaId.nome;
                } else if (userData.empresaAtual && userData.empresaAtual.nome) {
                    nomeEmpresa = userData.empresaAtual.nome;
                } else if (empresaIdExtraido) {
                    try {
                        console.log('🔍 Buscando empresa pelo ID:', empresaIdExtraido);
                        const empresaResponse = await axios.get(`${API_BASE_URL}/empresas/${empresaIdExtraido}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        if (empresaResponse.data && empresaResponse.data.success) {
                            const empresaData = empresaResponse.data.data || empresaResponse.data.empresa;
                            if (empresaData && empresaData.nome) {
                                nomeEmpresa = empresaData.nome;
                                console.log('✅ Nome da empresa encontrado via API:', nomeEmpresa);
                            }
                        }
                    } catch (err) {
                        console.warn('⚠️ Não foi possível buscar o nome da empresa:', err.message);
                    }
                }

                if (nomeEmpresa === 'Minha Empresa') {
                    const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                    if (storedUser && storedUser.empresaNome && storedUser.empresaNome !== 'Minha Empresa') {
                        nomeEmpresa = storedUser.empresaNome;
                    }
                }

                setEmpresaNome(nomeEmpresa);
                localStorage.setItem('usuario', JSON.stringify({
                    ...userData,
                    empresaNome: nomeEmpresa
                }));
                return userData;
            }
        } catch (err) {
            console.error('❌ Erro ao buscar utilizador:', err);
        }
        return null;
    };

    const fetchProdutos = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            const response = await api.get('/produtos');

            let produtosData = [];
            let config = { ...CONFIG_PADRAO };

            if (response.data) {
                if (response.data.success && response.data.data) {
                    produtosData = response.data.data;
                    if (response.data.configuracao) {
                        config = {
                            ...CONFIG_PADRAO,
                            ...response.data.configuracao,
                            moeda: {
                                ...CONFIG_PADRAO.moeda,
                                ...(response.data.configuracao.moeda || {})
                            }
                        };
                    }
                } else if (Array.isArray(response.data)) {
                    produtosData = response.data;
                }
            }

            setConfigEmpresa(config);
            const produtosOrdenados = ordenarPorData(produtosData);
            setProdutos(produtosOrdenados);
            calcularStats(produtosOrdenados);
            filtrarProdutos(produtosOrdenados, searchTerm, filtros);

        } catch (err) {
            console.error('❌ Erro ao carregar produtos:', err);
            showToast(err.response?.data?.erro || 'Erro ao carregar inventário', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategorias = async () => {
        try {
            const response = await api.get('/categorias');
            if (response.data) {
                const categoriasData = Array.isArray(response.data) ? response.data : response.data.data || [];
                setCategorias(categoriasData);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar categorias:', err);
        }
    };

    const fetchFornecedores = async () => {
        try {
            const response = await api.get('/fornecedores');
            if (response.data) {
                const fornecedoresData = Array.isArray(response.data) ? response.data : response.data.data || [];
                setFornecedores(fornecedoresData);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar fornecedores:', err);
        }
    };

    // ==============================================
    // 🔥 FUNÇÃO PARA BUSCAR ARMAZÉNS (CORRIGIDA)
    // ==============================================
    const fetchArmazens = async () => {
        try {
            console.log('🔍 Buscando armazéns...');

            // 🔥 CORREÇÃO: Usar a rota correta com /api/armazens
            // O api já tem baseURL = '/api'
            // Então o caminho completo será: /api/armazens
            const response = await api.get('/armazens');

            console.log('📦 Resposta da API:', response.data);

            if (response.data && response.data.success) {
                const armazensData = response.data.data || [];
                setArmazens(armazensData);
                console.log('🏢 Armazéns carregados:', armazensData.length);
                console.log('📦 Lista de armazéns:', armazensData);

                // Atualizar estatísticas
                setStats(prev => ({
                    ...prev,
                    armazensCount: armazensData.length
                }));
            } else {
                console.warn('⚠️ Resposta da API sem dados de armazéns:', response.data);
                setArmazens([]);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar armazéns:', err);
            console.error('📡 Detalhes do erro:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status,
                config: err.config
            });

            // Tentar buscar da empresa como fallback (se houver empresaId)
            if (empresaId) {
                try {
                    console.log('🔍 Tentando buscar armazéns da empresa como fallback...');
                    const empresaResponse = await api.get(`/empresas/${empresaId}`);
                    if (empresaResponse.data && empresaResponse.data.success) {
                        const empresaData = empresaResponse.data.data || empresaResponse.data.empresa;
                        if (empresaData && empresaData.armazens) {
                            setArmazens(empresaData.armazens);
                            console.log('🏢 Armazéns carregados da empresa:', empresaData.armazens.length);
                            setStats(prev => ({
                                ...prev,
                                armazensCount: empresaData.armazens.length
                            }));
                        } else {
                            setArmazens([]);
                        }
                    }
                } catch (err2) {
                    console.warn('⚠️ Não foi possível carregar armazéns da empresa:', err2.message);
                    setArmazens([]);
                }
            } else {
                setArmazens([]);
            }
        }
    };

    // ==============================================
    // FILTRAR PRODUTOS - COM ARMAZÉM
    // ==============================================
    const filtrarProdutos = useCallback((produtosList = produtos, term = searchTerm, filtrosList = filtros) => {
        let resultados = [...produtosList];

        if (term && term.trim() !== '') {
            const searchLower = term.toLowerCase().trim();
            resultados = resultados.filter(p => {
                const nome = p.nome || '';
                const codigo = p.codigo || '';
                const descricao = p.descricao || '';
                let categoria = '';
                if (p.categoriaId) {
                    if (typeof p.categoriaId === 'object') {
                        categoria = p.categoriaId.nome || '';
                    }
                }
                let fornecedor = '';
                if (p.fornecedorId) {
                    if (typeof p.fornecedorId === 'object') {
                        fornecedor = p.fornecedorId.nome || '';
                    }
                }
                let armazem = '';
                if (p.armazemId) {
                    if (typeof p.armazemId === 'object') {
                        armazem = p.armazemId.nome || '';
                    }
                }

                return nome.toLowerCase().includes(searchLower) ||
                    codigo.toLowerCase().includes(searchLower) ||
                    descricao.toLowerCase().includes(searchLower) ||
                    categoria.toLowerCase().includes(searchLower) ||
                    fornecedor.toLowerCase().includes(searchLower) ||
                    armazem.toLowerCase().includes(searchLower);
            });
        }

        if (filtrosList.categoria && filtrosList.categoria !== 'todos') {
            resultados = resultados.filter(p => {
                let catId = null;
                if (p.categoriaId) {
                    if (typeof p.categoriaId === 'string') {
                        catId = p.categoriaId;
                    } else if (typeof p.categoriaId === 'object') {
                        catId = p.categoriaId._id || p.categoriaId.id || null;
                    }
                }
                return catId === filtrosList.categoria;
            });
        }

        if (filtrosList.fornecedor && filtrosList.fornecedor !== 'todos') {
            resultados = resultados.filter(p => {
                let forId = null;
                if (p.fornecedorId) {
                    if (typeof p.fornecedorId === 'string') {
                        forId = p.fornecedorId;
                    } else if (typeof p.fornecedorId === 'object') {
                        forId = p.fornecedorId._id || p.fornecedorId.id || null;
                    }
                }
                return forId === filtrosList.fornecedor;
            });
        }

        // 🔥 NOVO FILTRO POR ARMAZÉM
        if (filtrosList.armazem && filtrosList.armazem !== 'todos') {
            resultados = resultados.filter(p => {
                let armId = null;
                if (p.armazemId) {
                    if (typeof p.armazemId === 'string') {
                        armId = p.armazemId;
                    } else if (typeof p.armazemId === 'object') {
                        armId = p.armazemId._id || p.armazemId.id || null;
                    }
                }
                return armId === filtrosList.armazem;
            });
        }

        if (filtrosList.statusStock && filtrosList.statusStock !== 'todos') {
            if (filtrosList.statusStock === 'sem_stock') {
                resultados = resultados.filter(p => p.stockAtual === 0);
            } else if (filtrosList.statusStock === 'baixo_stock') {
                resultados = resultados.filter(p => p.stockAtual > 0 && p.stockAtual <= (p.stockMinimo || 0));
            } else if (filtrosList.statusStock === 'stock_medio') {
                resultados = resultados.filter(p => p.stockAtual > (p.stockMinimo || 0) && p.stockAtual < 10);
            } else if (filtrosList.statusStock === 'stock_alto') {
                resultados = resultados.filter(p => p.stockAtual >= 10);
            }
        }

        if (filtrosList.unidadeMedida && filtrosList.unidadeMedida !== 'todos') {
            resultados = resultados.filter(p => p.unidadeMedida === filtrosList.unidadeMedida);
        }

        if (filtrosList.ativo && filtrosList.ativo !== 'todos') {
            const isAtivo = filtrosList.ativo === 'ativo';
            resultados = resultados.filter(p => p.ativo === isAtivo);
        }

        // Ordenação
        if (filtrosList.ordenarPor === 'nome') {
            resultados.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
        } else if (filtrosList.ordenarPor === 'stock') {
            resultados.sort((a, b) => (b.stockAtual || 0) - (a.stockAtual || 0));
        } else if (filtrosList.ordenarPor === 'preco') {
            resultados.sort((a, b) => (b.precoVenda || 0) - (a.precoVenda || 0));
        } else if (filtrosList.ordenarPor === 'valorStock') {
            resultados.sort((a, b) => {
                const valorA = (a.stockAtual || 0) * (a.precoVenda || 0);
                const valorB = (b.stockAtual || 0) * (b.precoVenda || 0);
                return valorB - valorA;
            });
        }

        // 🔥 MANTÉM ORDENAÇÃO POR DATA (MAIS RECENTES PRIMEIRO) como fallback
        resultados = ordenarPorData(resultados);

        setFilteredProdutos(resultados);

        const total = resultados.length;
        const pages = Math.ceil(total / pagination.limit) || 1;
        setPagination(prev => ({
            ...prev,
            total: total,
            pages: pages,
            page: Math.min(prev.page, pages)
        }));
    }, [produtos, searchTerm, filtros, pagination.limit]);

    // ==============================================
    // 🔥 EXPORTAÇÃO
    // ==============================================
    const exportarInventario = async (formato) => {
        if (formato === 'pdf') {
            await exportarInventarioPDF();
            return;
        }

        setExportLoading(true);
        setShowExportModal(false);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('Sessão expirada. Faça login novamente.');
                return;
            }

            const params = new URLSearchParams();
            params.append('formato', formato);
            if (searchTerm) params.append('search', searchTerm);
            if (filtros.categoria && filtros.categoria !== 'todos') params.append('categoria', filtros.categoria);
            if (filtros.fornecedor && filtros.fornecedor !== 'todos') params.append('fornecedor', filtros.fornecedor);
            if (filtros.armazem && filtros.armazem !== 'todos') params.append('armazem', filtros.armazem);
            if (filtros.statusStock && filtros.statusStock !== 'todos') params.append('statusStock', filtros.statusStock);
            if (filtros.unidadeMedida && filtros.unidadeMedida !== 'todos') params.append('unidadeMedida', filtros.unidadeMedida);
            if (filtros.ativo && filtros.ativo !== 'todos') params.append('ativo', filtros.ativo);

            const url = `${API_BASE_URL}/produtos/exportar?${params.toString()}`;

            const response = await axios({
                method: 'GET',
                url: url,
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            let extension = '';
            let mimeType = '';

            if (formato === 'excel' || formato === 'xlsx') {
                extension = 'xlsx';
                mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            } else if (formato === 'csv') {
                extension = 'csv';
                mimeType = 'text/csv';
            }

            const blob = new Blob([response.data], { type: mimeType });

            const urlDownload = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlDownload;
            link.download = `inventario_${new Date().toISOString().split('T')[0]}.${extension}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlDownload);

            showToast('Sucesso', `Inventário exportado em ${formato.toUpperCase()}!`, 'success');
        } catch (err) {
            console.error('❌ Erro na exportação:', err);
            showToast('Erro', err.message || 'Falha na exportação', 'danger');
        } finally {
            setExportLoading(false);
        }
    };

    // ==============================================
    // 🔥 EXPORTAR PDF
    // ==============================================
    const exportarInventarioPDF = async () => {
        if (filteredProdutos.length === 0) {
            showToast('Aviso', 'Não há produtos para exportar', 'warning');
            return;
        }

        setExporting(true);
        setShowExportModal(false);

        try {
            const { pdf } = await import('@react-pdf/renderer');

            const getFiltroDescricao = () => {
                const partes = [];
                if (searchTerm) partes.push(`Pesquisa: "${searchTerm}"`);
                if (filtros.categoria && filtros.categoria !== 'todos') {
                    const cat = categorias.find(c => c._id === filtros.categoria);
                    if (cat) partes.push(`Categoria: ${cat.nome}`);
                }
                if (filtros.fornecedor && filtros.fornecedor !== 'todos') {
                    const forn = fornecedores.find(f => f._id === filtros.fornecedor);
                    if (forn) partes.push(`Fornecedor: ${forn.nome}`);
                }
                if (filtros.armazem && filtros.armazem !== 'todos') {
                    const arm = armazens.find(a => a._id === filtros.armazem);
                    if (arm) partes.push(`Armazém: ${arm.nome}`);
                }
                if (filtros.statusStock && filtros.statusStock !== 'todos') {
                    const labels = {
                        'sem_stock': 'Sem Stock',
                        'baixo_stock': 'Stock Baixo',
                        'stock_medio': 'Stock Médio',
                        'stock_alto': 'Stock Alto'
                    };
                    partes.push(`Status: ${labels[filtros.statusStock] || filtros.statusStock}`);
                }
                if (filtros.unidadeMedida && filtros.unidadeMedida !== 'todos') {
                    const unid = UNIDADES_MEDIDA.find(u => u.value === filtros.unidadeMedida);
                    if (unid) partes.push(`Unidade: ${unid.label}`);
                }
                if (filtros.ativo && filtros.ativo !== 'todos') {
                    partes.push(`Ativo: ${filtros.ativo === 'ativo' ? 'Sim' : 'Não'}`);
                }
                return partes.length > 0 ? partes.join(' | ') : 'Todos os produtos';
            };

            const filtroDescricao = getFiltroDescricao();

            const doc = (
                <InventarioPDFDocument
                    produtos={filteredProdutos}
                    stats={stats}
                    configEmpresa={configEmpresa}
                    filtroDescricao={filtroDescricao}
                    empresaNome={empresaNome}
                />
            );

            const blob = await pdf(doc).toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `inventario_${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            showToast('Sucesso', 'Inventário exportado em PDF com sucesso!', 'success');
        } catch (err) {
            console.error('❌ Erro ao exportar inventário PDF:', err);
            showToast('Erro', 'Não foi possível exportar o inventário: ' + err.message, 'danger');
        } finally {
            setExporting(false);
        }
    };

    // ==============================================
    // HANDLERS
    // ==============================================
    const aplicarFiltros = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        filtrarProdutos(produtos, searchTerm, filtros);
    };

    const limparFiltros = () => {
        setFiltros({
            categoria: 'todos',
            fornecedor: 'todos',
            armazem: 'todos',
            statusStock: 'todos',
            unidadeMedida: 'todos',
            ativo: 'todos',
            ordenarPor: 'nome'
        });
        setSearchTerm('');
        setShowFiltros(false);
        setTimeout(() => {
            setPagination(prev => ({ ...prev, page: 1 }));
            filtrarProdutos(produtos, '', {
                categoria: 'todos',
                fornecedor: 'todos',
                armazem: 'todos',
                statusStock: 'todos',
                unidadeMedida: 'todos',
                ativo: 'todos',
                ordenarPor: 'nome'
            });
        }, 50);
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination(prev => ({ ...prev, page }));
    };

    // ==============================================
    // 🔥 RENDER MODAL DE EXPORTAÇÃO
    // ==============================================
    const renderExportModal = () => {
        if (!showExportModal) return null;

        return (
            <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
                <div className="modal-content-export modal-export"  onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header" style={{ borderRadius: '25px 25px 0 0' }} >
                        <h3><Icon name="file_download" size={20} /> Exportar Inventário</h3>
                        <button className="modal-close" onClick={() => setShowExportModal(false)}>×</button>
                    </div>
                    <div className="modal-body" style={{
                        backgroundColor: document.documentElement.classList.contains('dark-mode-variables')
                            ? 'rgba(0, 0, 0, 0.05) !important'
                            : 'white'
                    }}>
                        <p className="export-subtitle">Selecione o formato de exportação:</p>
                        <div className="export-options">
                            <button
                                className="export-option excel"
                                onClick={() => exportarInventario('excel')}
                                disabled={exportLoading || exporting}
                            >
                                <div className="export-icon"><Icon name="grid_on" size={24} /></div>
                                <span className="export-label">Excel</span>
                                <small className="export-extension">.xlsx</small>
                            </button>
                            <button
                                className="export-option csv"
                                onClick={() => exportarInventario('csv')}
                                disabled={exportLoading || exporting}
                            >
                                <div className="export-icon"><Icon name="description" size={24} /></div>
                                <span className="export-label">CSV</span>
                                <small className="export-extension">.csv</small>
                            </button>
                            <button
                                className="export-option pdf"
                                onClick={() => exportarInventario('pdf')}
                                disabled={exportLoading || exporting}
                            >
                                <div className="export-icon"><Icon name="picture_as_pdf" size={24} /></div>
                                <span className="export-label">PDF</span>
                                <small className="export-extension">.pdf</small>
                            </button>
                        </div>
                        {(exportLoading || exporting) && (
                            <div className="export-loading">
                                <div className="spinner-small"></div>
                                <p>{exporting ? 'A gerar PDF...' : 'A exportar...'}</p>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer" style={{ borderRadius: '0 0 25px 25px ' }}>
                        <button className="modal-btn-secondary" onClick={() => setShowExportModal(false)}>
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================
    // EFECTOS
    // ==============================================
    useEffect(() => {
        const carregarDados = async () => {
            await fetchLoggedUser();
            await Promise.all([
                fetchProdutos(),
                fetchCategorias(),
                fetchFornecedores(),
                fetchArmazens() // 🔥 CARREGAR ARMAZÉNS
            ]);
        };
        carregarDados();
    }, []);

    useEffect(() => {
        if (produtos.length > 0) {
            filtrarProdutos(produtos, searchTerm, filtros);
        }
    }, [searchTerm, filtros, produtos, filtrarProdutos]);

    // ==============================================
    // PAGINAÇÃO FRONTEND
    // ==============================================
    const currentProdutos = useMemo(() => {
        const startIndex = (pagination.page - 1) * pagination.limit;
        const endIndex = startIndex + pagination.limit;
        return filteredProdutos.slice(startIndex, endIndex);
    }, [filteredProdutos, pagination.page, pagination.limit]);

    // ==============================================
    // FUNÇÃO PARA GERAR NÚMEROS DE PÁGINA COM "..."
    // ==============================================
    const getPageNumbers = (currentPage, totalPages) => {
        const delta = 2; // 🔥 Número de páginas antes e depois da atual

        // 🔥 Se o total de páginas for menor ou igual a 6, mostra todas
        if (totalPages <= 6) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const range = [];
        const rangeWithDots = [];
        let l;

        // 🔥 Páginas à esquerda da página atual
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }

        // 🔥 Adicionar "..." entre os números
        for (let i of range) {
            if (l) {
                if (i - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (i - l !== 1) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            l = i;
        }

        return rangeWithDots;
    };

    // ==============================================
    // RENDER PAGINAÇÃO (CORRIGIDA)
    // ==============================================
    const renderPagination = () => {
        const { page, pages, total, limit } = pagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * limit + 1;
        const endItem = Math.min(page * limit, total);
        const pageNumbers = getPageNumbers(page, pages);

        return (
            <div className="inventario-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} produtos</span>
                </div>
                <div className="pagination-controls">
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        title="Página anterior"
                    >
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {pageNumbers.map((p, index) => {
                            if (p === '...') {
                                return (
                                    <span key={`dots-${index}`} className="pagination-dots">
                                        …
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={p}
                                    className={`pagination-number ${page === p ? 'active' : ''}`}
                                    onClick={() => handlePageChange(p)}
                                >
                                    {p}
                                </button>
                            );
                        })}
                    </div>
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === pages}
                        title="Próxima página"
                    >
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // RENDER PRINCIPAL
    // ==============================================
    return (
        <div className="inventario-container">
            {/* 🔥 MODAL DE EXPORTAÇÃO */}
            {renderExportModal()}

            {/* MENSAGENS */}
            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={16} />
                    <span>{message.text}</span>
                    <button className="alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                </div>
            )}

            {/* HEADER */}
            <div className="inventario-header">
                <div className="header-title">
                    <h2><Icon name="inventory_2" size={24} /> Inventário</h2>
                    <p>Visualize e gerencie todo o stock da sua empresa</p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-export"
                        onClick={() => setShowExportModal(true)}
                        disabled={exportLoading || exporting}
                        title="Exportar Inventário"
                    >
                        <Icon name="file_download" size={20} />
                        {exportLoading || exporting ? 'A exportar...' : 'Exportar'}
                    </button>
                    <button
                        className="btn-refresh"
                        onClick={fetchProdutos}
                        title="Atualizar"
                    >
                        <Icon name="refresh" size={18} />
                    </button>
                </div>
            </div>

            {/* STATS CARDS */}
            {!loading && (
                <div className="inventario-stats-grid">
                    <div className="stat-card total">
                        <div className="stat-icon"><Icon name="inventory_2" size={16} /></div>
                        <div className="stat-info">
                            <span className="stat-value">{formatarNumero(stats.totalProdutos)}</span>
                            <span className="stat-label">Total Produtos</span>
                        </div>
                    </div>
                    <div className="stat-card stock">
                        <div className="stat-icon"><Icon name="inbox" size={16} /></div>
                        <div className="stat-info">
                            <span className="stat-value">{formatarNumero(stats.totalStock)}</span>
                            <span className="stat-label">Unidades em Stock</span>
                        </div>
                    </div>
                    <div className="stat-card value">
                        <div className="stat-icon"><Icon name="attach_money" size={16} /></div>
                        <div className="stat-info">
                            <span className="stat-value">{formatarMoeda(stats.valorTotalStock)}</span>
                            <span className="stat-label">Valor Total em Stock</span>
                        </div>
                    </div>
                    <div className="stat-card categories">
                        <div className="stat-icon"><Icon name="category" size={16} /></div>
                        <div className="stat-info">
                            <span className="stat-value">{stats.categoriasCount}</span>
                            <span className="stat-label">Categorias</span>
                        </div>
                    </div>
                    <div className="stat-card suppliers">
                        <div className="stat-icon"><Icon name="business" size={16} /></div>
                        <div className="stat-info">
                            <span className="stat-value">{stats.fornecedoresCount}</span>
                            <span className="stat-label">Fornecedores</span>
                        </div>
                    </div>
                    <div className="stat-card armazens"> {/* 🔥 NOVO CARD */}
                        <div className="stat-icon"><Icon name="store" size={16} /></div>
                        <div className="stat-info">
                            <span className="stat-value">{stats.armazensCount || 0}</span>
                            <span className="stat-label">Armazéns</span>
                        </div>
                    </div>
                    <div className="stat-card warning">
                        <div className="stat-icon"><Icon name="warning" size={16} /></div>
                        <div className="stat-info">
                            <span className="stat-value">{stats.produtosBaixoStock + stats.produtosSemStock}</span>
                            <span className="stat-label">Stock Crítico</span>
                        </div>
                    </div>
                </div>
            )}

            {/* FILTROS */}
            <div className="inventario-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input
                        type="text"
                        placeholder="Pesquisar por nome, código, descrição, categoria, fornecedor ou armazém..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && aplicarFiltros()}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="filter-actions">
                    <button
                        className={`btn-filter ${showFiltros ? 'active' : ''}`}
                        onClick={() => setShowFiltros(!showFiltros)}
                    >
                        <Icon name="filter_list" size={18} />
                        {showFiltros ? 'Ocultar Filtros' : 'Filtros'}
                    </button>
                    <button className="btn-refresh" onClick={fetchProdutos} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                </div>
            </div>

            {/* FILTROS AVANÇADOS COM ARMAZÉM */}
            {showFiltros && (
                <div className="inventario-filtros-avancados">
                    <div className="filtros-row">
                        <div className="filtro-group">
                            <label>Categoria</label>
                            <select
                                value={filtros.categoria}
                                onChange={(e) => setFiltros({ ...filtros, categoria: e.target.value })}
                            >
                                <option value="todos">Todas as Categorias</option>
                                {categorias.map(cat => (
                                    <option key={cat._id} value={cat._id}>{cat.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Fornecedor</label>
                            <select
                                value={filtros.fornecedor}
                                onChange={(e) => setFiltros({ ...filtros, fornecedor: e.target.value })}
                            >
                                <option value="todos">Todos os Fornecedores</option>
                                {fornecedores.map(f => (
                                    <option key={f._id} value={f._id}>{f.nome}</option>
                                ))}
                            </select>
                        </div>
                        {/* 🔥 NOVO FILTRO DE ARMAZÉM */}
                        <div className="filtro-group">
                            <label>Armazém</label>
                            <select
                                value={filtros.armazem}
                                onChange={(e) => setFiltros({ ...filtros, armazem: e.target.value })}
                            >
                                <option value="todos">Todos os Armazéns</option>
                                {armazens.map(a => (
                                    <option key={a._id} value={a._id}>{a.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Status Stock</label>
                            <select
                                value={filtros.statusStock}
                                onChange={(e) => setFiltros({ ...filtros, statusStock: e.target.value })}
                            >
                                <option value="todos">Todos</option>
                                <option value="sem_stock">🔴 Sem Stock</option>
                                <option value="baixo_stock">🟡 Stock Baixo</option>
                                <option value="stock_medio">🔵 Stock Médio</option>
                                <option value="stock_alto">🟢 Stock Alto</option>
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Unidade de Medida</label>
                            <select
                                value={filtros.unidadeMedida}
                                onChange={(e) => setFiltros({ ...filtros, unidadeMedida: e.target.value })}
                            >
                                <option value="todos">Todas</option>
                                {UNIDADES_MEDIDA.map(u => (
                                    <option key={u.value} value={u.value}>{u.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Ordenar Por</label>
                            <select
                                value={filtros.ordenarPor}
                                onChange={(e) => setFiltros({ ...filtros, ordenarPor: e.target.value })}
                            >
                                <option value="nome">Nome</option>
                                <option value="stock">Quantidade em Stock</option>
                                <option value="preco">Preço de Venda</option>
                                <option value="valorStock">Valor em Stock</option>
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Status</label>
                            <select
                                value={filtros.ativo}
                                onChange={(e) => setFiltros({ ...filtros, ativo: e.target.value })}
                            >
                                <option value="todos">Todos</option>
                                <option value="ativo">✅ Ativos</option>
                                <option value="inativo">❌ Inativos</option>
                            </select>
                        </div>
                        <button className="btn-aplicar-filtros" onClick={aplicarFiltros}>
                            <Icon name="filter_list" size={16} /> Aplicar
                        </button>
                        <button className="btn-limpar-filtros" onClick={limparFiltros}>
                            <Icon name="clear" size={16} /> Limpar
                        </button>
                    </div>
                </div>
            )}

            {/* TABELA DE INVENTÁRIO - CORRIGIDA (SEM ESPAÇOS EM BRANCO ENTRE TAGS) */}
            {loading ? (
                <div className="loading-container">
                    <div className="spinner-large"></div>
                    <p>A carregar inventário...</p>
                </div>
            ) : filteredProdutos.length === 0 ? (
                <div className="empty-state">
                    <Icon name="inventory_2" size={48} />
                    <p>Nenhum produto encontrado</p>
                    <p className="empty-subtext">Tente ajustar os filtros ou adicionar produtos</p>
                </div>
            ) : (
                <div className="inventario-table-container">
                    <table className="inventario-table">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Código</th>
                                <th>Produto</th>
                                <th>Categoria</th>
                                <th>Fornecedor</th>
                                <th>Armazém</th>
                                <th>Stock</th>
                                <th>Preço Venda</th>
                                <th>Valor Stock</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentProdutos.map(produto => {
                                const status = getStatusStock(produto.stockAtual, produto.stockMinimo);
                                const isAtivo = produto.ativo !== false;
                                const valorStock = (produto.stockAtual || 0) * (produto.precoVenda || 0);

                                let categoriaNome = '-';
                                if (produto.categoriaId) {
                                    if (typeof produto.categoriaId === 'object') {
                                        categoriaNome = produto.categoriaId.nome || '-';
                                    }
                                }

                                let fornecedorNome = '-';
                                if (produto.fornecedorId) {
                                    if (typeof produto.fornecedorId === 'object') {
                                        fornecedorNome = produto.fornecedorId.nome || '-';
                                    }
                                }

                                let armazemNome = '-';
                                if (produto.armazemId) {
                                    if (typeof produto.armazemId === 'object') {
                                        armazemNome = produto.armazemId.nome || '-';
                                    }
                                }

                                const dataCriacao = formatarData(produto.createdAt);

                                return (
                                    <tr key={produto._id} className={!isAtivo ? 'inactive-row' : ''}>
                                        <td>
                                            <span className="data-hora-badge">
                                                <span className="data">{dataCriacao.split(' ')[0]}</span>
                                                <span className="hora">{dataCriacao.split(' ')[1]}</span>
                                            </span>
                                        </td>
                                        <td><span className="codigo-badge">{produto.codigo || '-'}</span></td>
                                        <td>
                                            <div className="produto-info">
                                                <strong>{produto.nome}</strong>
                                                {!isAtivo && (
                                                    <span className="status-badge inactive" style={{ fontSize: '0.5rem', padding: '0.1rem 0.4rem' }}>
                                                        Inativo
                                                    </span>
                                                )}
                                                {produto.descricao && (
                                                    <span className="produto-descricao">{produto.descricao}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>{categoriaNome}</td>
                                        <td>{fornecedorNome}</td>
                                        <td>
                                            <span className="armazem-badge">{armazemNome}</span>
                                        </td>
                                        <td>
                                            <span className={`stock-value ${status.class}`}>
                                                {formatarNumero(produto.stockAtual)}
                                            </span>
                                        </td>
                                        <td className="preco-cell">{formatarMoeda(produto.precoVenda)}</td>
                                        <td className="valor-cell">{formatarMoeda(valorStock)}</td>
                                        <td>
                                            <span className={`status-badge ${status.class}`}>
                                                <Icon name={status.icon} size={12} />
                                                {status.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {renderPagination()}
                </div>
            )}
        </div>
    );
}